import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Globe, AlertTriangle, Shield, BarChart3, Clock } from 'lucide-react';
import { API_URL } from '@/services/apiClient';

interface TrafficData {
  topDomains: Array<{ domain: string; count: number }>;
  eventTypes: Array<{ type: string; count: number }>;
  flaggedCount: number;
  totalCount: number;
  safeCount: number;
  hourlyActivity: Array<{ hour: number; count: number }>;
}

function SiteFavicon({ domain, dm }: { domain: string; dm: boolean }) {
  const [failed, setFailed] = useState(false);
  const clean = domain.replace(/^https?:\/\//, '').split('/')[0];
  if (!clean || failed) {
    return <Globe className={`h-4 w-4 shrink-0 ${dm ? 'text-slate-500' : 'text-slate-400'}`} />;
  }
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(clean)}&sz=32`}
      alt=""
      className="h-4 w-4 shrink-0 rounded-sm object-contain"
      onError={() => setFailed(true)}
    />
  );
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  page_visit: 'Page Visits',
  search: 'Searches',
  typed_content: 'Typed Content',
  app_usage: 'App Usage',
  download: 'Downloads',
  file_access: 'File Access',
};

const EVENT_TYPE_COLORS: Record<string, string> = {
  page_visit: 'bg-blue-500',
  search: 'bg-purple-500',
  typed_content: 'bg-amber-500',
  app_usage: 'bg-emerald-500',
  download: 'bg-orange-500',
  file_access: 'bg-pink-500',
};

function HourlyLinePlot({
  hourlyActivity, maxHour, dm, mutedCls,
}: {
  hourlyActivity: Array<{ hour: number; count: number }>;
  maxHour: number;
  dm: boolean;
  mutedCls: string;
}) {
  const [tooltip, setTooltip] = useState<{ x: number; y: number; hour: number; count: number } | null>(null);

  const W = 600;
  const H = 120;
  const PAD = { top: 12, right: 12, bottom: 20, left: 28 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const counts = Array.from({ length: 24 }, (_, h) => {
    const entry = hourlyActivity.find(x => x.hour === h);
    return entry?.count ?? 0;
  });

  const xOf = (h: number) => PAD.left + (h / 23) * innerW;
  const yOf = (c: number) => PAD.top + innerH - (maxHour > 0 ? (c / maxHour) * innerH : 0);

  const linePath = counts
    .map((c, h) => `${h === 0 ? 'M' : 'L'}${xOf(h).toFixed(1)},${yOf(c).toFixed(1)}`)
    .join(' ');

  const areaPath =
    `M${xOf(0).toFixed(1)},${(PAD.top + innerH).toFixed(1)} ` +
    counts.map((c, h) => `L${xOf(h).toFixed(1)},${yOf(c).toFixed(1)}`).join(' ') +
    ` L${xOf(23).toFixed(1)},${(PAD.top + innerH).toFixed(1)} Z`;

  const schoolX1 = xOf(8);
  const schoolX2 = xOf(15);

  const trackCls = dm ? '#334155' : '#e2e8f0';
  const textCls  = dm ? '#64748b' : '#94a3b8';

  return (
    <div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        style={{ height: 140 }}
        onMouseLeave={() => setTooltip(null)}
      >
        {/* School hours shading */}
        <rect
          x={schoolX1} y={PAD.top}
          width={schoolX2 - schoolX1} height={innerH}
          fill={dm ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.06)'}
        />

        {/* Y grid lines */}
        {[0, 0.5, 1].map(f => {
          const y = PAD.top + innerH * (1 - f);
          return (
            <g key={f}>
              <line x1={PAD.left} y1={y} x2={PAD.left + innerW} y2={y}
                stroke={trackCls} strokeWidth={1} strokeDasharray={f > 0 ? '3 3' : undefined} />
              {f > 0 && (
                <text x={PAD.left - 4} y={y + 3.5} textAnchor="end"
                  fontSize={9} fill={textCls}>
                  {Math.round(maxHour * f)}
                </text>
              )}
            </g>
          );
        })}

        {/* Area fill */}
        <path d={areaPath} fill="url(#lineGrad)" opacity={0.4} />

        {/* Line */}
        <path d={linePath} fill="none" stroke="#3b82f6" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />

        {/* Dots + hover targets */}
        {counts.map((c, h) => (
          <circle
            key={h}
            cx={xOf(h)} cy={yOf(c)} r={c > 0 ? 3 : 2}
            fill={c > 0 ? '#3b82f6' : trackCls}
            stroke={dm ? '#1e293b' : '#fff'} strokeWidth={1.5}
            className="cursor-pointer"
            onMouseEnter={e => {
              const svg = (e.target as SVGCircleElement).ownerSVGElement!;
              const rect = svg.getBoundingClientRect();
              const scaleX = rect.width / W;
              const scaleY = rect.height / H;
              setTooltip({ x: xOf(h) * scaleX, y: yOf(c) * scaleY, hour: h, count: c });
            }}
          />
        ))}

        {/* X axis labels */}
        {[0, 6, 12, 18, 23].map(h => (
          <text key={h} x={xOf(h)} y={H - 4} textAnchor="middle" fontSize={9} fill={textCls}>
            {h === 0 ? '12am' : h === 12 ? '12pm' : h === 23 ? '11pm' : `${h > 12 ? h - 12 : h}${h >= 12 ? 'pm' : 'am'}`}
          </text>
        ))}

        <defs>
          <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.5} />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
          </linearGradient>
        </defs>
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div
          className={`pointer-events-none absolute z-10 text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap
            ${dm ? 'bg-slate-700 text-slate-100' : 'bg-slate-800 text-white'}`}
          style={{ left: tooltip.x + 8, top: tooltip.y - 28, position: 'absolute' }}
        >
          {tooltip.hour === 0 ? '12am' : tooltip.hour < 12 ? `${tooltip.hour}am` : tooltip.hour === 12 ? '12pm' : `${tooltip.hour - 12}pm`}
          {' — '}{tooltip.count} events
        </div>
      )}

      <div className="flex items-center gap-4 mt-1">
        <div className="flex items-center gap-1.5">
          <div className="h-2.5 w-2.5 rounded-sm bg-blue-500 opacity-20" />
          <span className={`text-xs ${mutedCls}`}>School hours (8am–3pm)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-0.5 w-4 bg-blue-500 rounded" />
          <span className={`text-xs ${mutedCls}`}>Activity</span>
        </div>
      </div>
    </div>
  );
}

export function WebTrafficDashboard({ dm }: { dm: boolean }) {
  const [data, setData] = useState<TrafficData | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const cardCls = `border shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const titleCls = dm ? 'text-slate-100' : 'text-slate-800';
  const descCls = dm ? 'text-slate-400' : 'text-slate-500';
  const mutedCls = dm ? 'text-slate-500' : 'text-slate-400';

  const fetch_ = useCallback(async () => {
    setLoading(true);
    const token = sessionStorage.getItem('authToken');
    try {
      const res = await fetch(`${API_URL}/dashboard/school-admin/web-traffic?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch {/* */} finally { setLoading(false); }
  }, [days]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const maxDomain = Math.max(...(data?.topDomains.map(d => d.count) ?? [1]), 1);
  const maxHour = Math.max(...(data?.hourlyActivity.map(h => h.count) ?? [1]), 1);
  const flagPct = data && data.totalCount > 0 ? Math.round((data.flaggedCount / data.totalCount) * 100) : 0;

  return (
    <div className="space-y-5" data-dm={dm}>
      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className={`text-lg font-semibold ${titleCls}`}>Web Traffic & Browser Activity</h2>
        <div className="flex gap-2">
          {[7, 14, 30].map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={`btn-glass btn-glass-pill text-sm ${days === d ? 'btn-glass-blue' : ''}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : (
        <>
          {/* Stats row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className={cardCls}>
              <CardContent className="pt-5">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${dm ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                    <Globe className="h-5 w-5 text-blue-500" />
                  </div>
                  <div>
                    <p className={`text-xs ${descCls}`}>Total Events</p>
                    <p className={`text-2xl font-bold ${titleCls}`}>{data?.totalCount.toLocaleString() ?? 0}</p>
                    <p className={`text-xs ${mutedCls}`}>Last {days} days</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={cardCls}>
              <CardContent className="pt-5">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${dm ? 'bg-red-900/50' : 'bg-red-100'}`}>
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                  </div>
                  <div>
                    <p className={`text-xs ${descCls}`}>Flagged Events</p>
                    <p className={`text-2xl font-bold ${titleCls}`}>{data?.flaggedCount.toLocaleString() ?? 0}</p>
                    <p className={`text-xs text-red-500`}>{flagPct}% of all traffic</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={cardCls}>
              <CardContent className="pt-5">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${dm ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
                    <Shield className="h-5 w-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className={`text-xs ${descCls}`}>Safe Events</p>
                    <p className={`text-2xl font-bold ${titleCls}`}>{data?.safeCount.toLocaleString() ?? 0}</p>
                    <p className={`text-xs text-emerald-500`}>{100 - flagPct}% clean</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Top Domains */}
            <Card className={cardCls}>
              <CardHeader className="pb-3">
                <CardTitle className={`text-base ${titleCls}`}>Top Visited Domains</CardTitle>
                <CardDescription className={descCls}>Most accessed sites school-wide</CardDescription>
              </CardHeader>
              <CardContent>
                {data?.topDomains.length ? (
                  <div className="space-y-3">
                    {data.topDomains.map((d, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <SiteFavicon domain={d.domain} dm={dm} />
                        <span className={`text-xs w-36 truncate shrink-0 ${dm ? 'text-slate-300' : 'text-slate-700'}`}>{d.domain}</span>
                        <div className={`flex-1 h-2 rounded-full overflow-hidden ${dm ? 'bg-slate-700' : 'bg-slate-100'}`}>
                          <div className="h-full rounded-full bg-blue-500 transition-all duration-500"
                            style={{ width: `${Math.round((d.count / maxDomain) * 100)}%` }} />
                        </div>
                        <span className={`text-xs font-semibold w-8 text-right shrink-0 ${dm ? 'text-slate-300' : 'text-slate-700'}`}>{d.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={`flex flex-col items-center justify-center h-32 ${mutedCls}`}>
                    <Globe className="h-8 w-8 mb-2 opacity-40" />
                    <p className="text-sm">No traffic data yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Event Type Breakdown */}
            <Card className={cardCls}>
              <CardHeader className="pb-3">
                <CardTitle className={`text-base ${titleCls}`}>Activity Breakdown</CardTitle>
                <CardDescription className={descCls}>Events by type</CardDescription>
              </CardHeader>
              <CardContent>
                {data?.eventTypes.length ? (
                  <div className="space-y-3">
                    {data.eventTypes.sort((a, b) => b.count - a.count).map((e, i) => {
                      const total = data.totalCount || 1;
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <span className={`text-xs w-28 shrink-0 ${dm ? 'text-slate-400' : 'text-slate-600'}`}>
                            {EVENT_TYPE_LABELS[e.type] || e.type}
                          </span>
                          <div className={`flex-1 h-2 rounded-full overflow-hidden ${dm ? 'bg-slate-700' : 'bg-slate-100'}`}>
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${EVENT_TYPE_COLORS[e.type] || 'bg-slate-400'}`}
                              style={{ width: `${Math.round((e.count / total) * 100)}%` }}
                            />
                          </div>
                          <span className={`text-xs font-semibold w-8 text-right shrink-0 ${dm ? 'text-slate-300' : 'text-slate-700'}`}>{e.count}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className={`flex flex-col items-center justify-center h-32 ${mutedCls}`}>
                    <BarChart3 className="h-8 w-8 mb-2 opacity-40" />
                    <p className="text-sm">No event data yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Hourly Activity Line Plot */}
          <Card className={cardCls}>
            <CardHeader className="pb-3">
              <CardTitle className={`text-base flex items-center gap-2 ${titleCls}`}>
                <Clock className="h-4 w-4" />
                Activity by Hour of Day
              </CardTitle>
              <CardDescription className={descCls}>When students are most active online</CardDescription>
            </CardHeader>
            <CardContent>
              {data?.hourlyActivity.length ? (
                <HourlyLinePlot hourlyActivity={data.hourlyActivity} maxHour={maxHour} dm={dm} mutedCls={mutedCls} />
              ) : (
                <div className={`flex flex-col items-center justify-center h-24 ${mutedCls}`}>
                  <p className="text-sm">No hourly data yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
